import { useMemo, useState, useEffect, useRef } from 'react'
import { useAccounts, useDebts, useGoals, useBudgets, useBills, useIncome, useQuickSpends, useQuickIncome, useMonthlyReports, useInvestments } from '../hooks/useData'
import { useStore } from '../store'
import { useAuth } from '../hooks/useAuth'
import { fmt, colors, fonts, CyclePicker } from '../components/UI'
import { getPayCycle, cyclesForPicker, cycleRangeLabel, billDueDateInMonth } from '../lib/payday'
import { calcHealthScore } from '../components/HealthScore'
import ReadinessCoach from '../components/ReadinessCoach'
import { checkMilestone } from '../components/MilestoneCelebration'
import MilestoneCelebration from '../components/MilestoneCelebration'
import WalkthroughOverlay from '../components/WalkthroughOverlay'
import NotificationPanel, { generateNotifications } from '../components/NotificationPanel'
import Hint from '../components/Hint'
import CycleReview from '../components/CycleReview'
import SpendingInContext from '../components/SpendingInContext'
import { buildCycleReview } from '../lib/cycleReview'
import QuickSpendModal from '../components/QuickSpendModal'
import BillLogo, { CAT_ICON } from '../components/BillLogo'
import { useIsMobile } from '../components/ResponsiveGrid'

// ── Helpers ──────────────────────────────────────────────────────────────────
function greeting(name) {
  const h = new Date().getHours()
  const t = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
  const first = (name || '').split(' ')[0]
  return first ? `${t}, ${first}` : t
}
function daysUntil(iso) { return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000)) }
function cyclePct(s, e) {
  const total = new Date(e) - new Date(s), elapsed = new Date() - new Date(s)
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
}

// ── Icon circle ───────────────────────────────────────────────────────────────
function IC({ emoji, color, size = 42 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.48), flexShrink: 0 }}>
      {emoji}
    </div>
  )
}

// ── Ring ──────────────────────────────────────────────────────────────────────
function Ring({ pct, size = 80, stroke = 7, color = colors.green, label, sublabel }) {
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'absolute', inset: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ - (Math.max(0, pct) / 100) * circ} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {label && <div style={{ fontFamily: fonts.heading, fontSize: '14px', fontWeight: 800, color, lineHeight: 1 }}>{label}</div>}
        {sublabel && <div style={{ fontSize: '10px', color: `rgba(${colors.textRgb},0.64)`, fontWeight: 600, lineHeight: 1.2, marginTop: '2px', textAlign: 'center' }}>{sublabel}</div>}
      </div>
    </div>
  )
}

// ── Metric tile ────────────────────────────────────────────────────────────────
function Tile({ emoji, iconColor, label, value, sub, accent, onClick, valueColor, hint }) {
  return (
    <button onClick={onClick} style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', padding: '14px', textAlign: 'left', cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative', overflow: 'hidden', width: '100%', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <IC emoji={emoji} color={iconColor} size={38} />
        <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
      </div>
      <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>{label}{hint && <Hint title={hint.title} text={hint.text} />}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: valueColor || colors.text, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>{sub}</div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: accent, borderRadius: '0 0 16px 16px' }} />
    </button>
  )
}

// ── Quick spend edit modal ────────────────────────────────────────────────────
function QuickIncomeEditModal({ entry, onSave, onDelete, onClose }) {
  const [amount, setAmount] = useState(String(entry.amount || ''))
  const [note, setNote] = useState(entry.note || '')
  const [saving, setSaving] = useState(false)
  async function handleSave() { setSaving(true); await onSave(entry.id, { amount: parseFloat(amount) || entry.amount, note: note.trim() || null }); setSaving(false); onClose() }
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, width: '100%', maxWidth: '480px', margin: '0 20px', borderRadius: '24px', padding: '8px 0 36px' }}>
        <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '8px auto 20px' }} />
        <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '17px', color: colors.text, padding: '0 20px 16px' }}>Edit income</div>
        <div style={{ padding: '0 20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '6px' }}>AMOUNT</div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '18px' }}>£</span>
              <input type="number" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.heading, fontSize: '24px', fontWeight: 700, padding: '12px 14px 12px 34px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '6px' }}>NOTE</div>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="What was this?" maxLength={80} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.body, fontSize: '14px', padding: '11px 14px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
            <button onClick={() => { onDelete(entry.id); onClose() }} style={{ padding: '13px', background: 'rgba(224,82,82,0.1)', border: '1px solid rgba(224,82,82,0.25)', borderRadius: '12px', color: colors.red, fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '13px', background: colors.green, border: 'none', borderRadius: '12px', color: '#fff', fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save changes'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function QuickSpendEditModal({ spend, onSave, onDelete, onClose }) {
  const [amount, setAmount] = useState(String(spend.amount || ''))
  const [note, setNote] = useState(spend.note || '')
  const [saving, setSaving] = useState(false)
  async function handleSave() { setSaving(true); await onSave(spend.id, { amount: parseFloat(amount) || spend.amount, note: note.trim() || null }); setSaving(false); onClose() }
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, width: '100%', maxWidth: '480px', margin: '0 20px', borderRadius: '24px', padding: '8px 0 36px' }}>
        <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '8px auto 20px' }} />
        <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '17px', color: colors.text, padding: '0 20px 16px' }}>Edit spend</div>
        <div style={{ padding: '0 20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '6px' }}>AMOUNT</div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '18px' }}>£</span>
              <input type="number" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.heading, fontSize: '24px', fontWeight: 700, padding: '12px 14px 12px 34px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '6px' }}>NOTE</div>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="What was this for?" maxLength={80} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: colors.text, fontFamily: fonts.body, fontSize: '14px', padding: '11px 14px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
            <button onClick={() => { onDelete(spend.id); onClose() }} style={{ padding: '13px', background: 'rgba(224,82,82,0.1)', border: '1px solid rgba(224,82,82,0.25)', borderRadius: '12px', color: colors.red, fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '13px', background: colors.gold, border: 'none', borderRadius: '12px', color: colors.onAccent, fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save changes'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Dashboard({ onNavigate, basicMode = false }) {
  const { profile, user } = useAuth()
  const isMobile = useIsMobile()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const savingsRateTarget = profile?.savings_rate_target || 20

  const cycles = useMemo(() => cyclesForPicker(payDay, shiftWeekends), [payDay, shiftWeekends])
  const currentCycle = useMemo(() => getPayCycle(payDay, new Date(), shiftWeekends), [payDay, shiftWeekends])
  const [selectedCycleStart, setSelectedCycleStart] = useState(null)
  const [showCyclePicker, setShowCyclePicker] = useState(false)
  const selectedCycle = useMemo(() => cycles.find(c => c.startIso === selectedCycleStart) || currentCycle, [cycles, selectedCycleStart, currentCycle])
  const signupMonth = profile?.created_at ? new Date(profile.created_at) : null
  const filteredCycles = useMemo(() => {
    if (!signupMonth) return cycles
    return cycles.filter(c => new Date(c.startIso) >= new Date(signupMonth.getFullYear(), signupMonth.getMonth(), 1))
  }, [cycles, signupMonth])
  const isCurrentCycle = !selectedCycleStart || selectedCycleStart === currentCycle?.startIso

  const { accounts, upsertAccount } = useAccounts()
  const { goals } = useGoals()
  const { monthlyContributions: investmentContributions } = useInvestments()
  const { debts } = useDebts()
  const { budgets, totalBudgeted } = useBudgets(selectedCycle?.startIso || '')
  const { getUpcoming, bills } = useBills()
  const { incomeEntries, incomeForCycle, recurringMonthly } = useIncome()
  const cycleKey = currentCycle?.startIso || ''
  const { cycleSpends, cycleTotal: quickSpendTotal, categorySpends, updateQuickSpend, deleteQuickSpend } = useQuickSpends(cycleKey)
  const { cycleIncome, cycleTotal: quickIncomeTotal, updateQuickIncome, deleteQuickIncome } = useQuickIncome(cycleKey)
  const { reports } = useMonthlyReports()

  const [showQuickSpend, setShowQuickSpend] = useState(false)
  const [quickSpendTab, setQuickSpendTab] = useState('spend')
  const [showWalkthrough, setShowWalkthrough] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tic_notifications_dismissed') || '[]') } catch { return [] }
  })
  const [tourPromptDismissed, setTourPromptDismissed] = useState(() => {
    try { return !!localStorage.getItem(`tic_walkthrough_complete_${user?.id || ''}`) } catch { return false }
  })
  function startWalkthrough() {
    setTourPromptDismissed(true)
    setShowWalkthrough(true)
  }
  function completeWalkthrough() {
    try { localStorage.setItem(`tic_walkthrough_complete_${user?.id || ''}`, '1') } catch {}
    setShowWalkthrough(false)
  }

  // Auto-start walkthrough for first-time users
  useEffect(() => {
    try {
      if (!localStorage.getItem(`tic_walkthrough_complete_${user?.id || ''}`)) {
        const t = setTimeout(() => {
          setTourPromptDismissed(true)
          setShowWalkthrough(true)
        }, 1500)
        return () => clearTimeout(t)
      }
    } catch {}
  }, [user])

  // ── Payday prompt (A) ─────────────────────────────────────────────────
  const promptKey = `tic_payday_prompt_${currentCycle?.startIso || ''}`
  const [paydayPromptDismissed, setPaydayPromptDismissed] = useState(() => {
    try { return !!localStorage.getItem(promptKey) } catch { return false }
  })
  function dismissPaydayPrompt() {
    try { localStorage.setItem(promptKey, '1') } catch {}
    setPaydayPromptDismissed(true)
  }
  const cycleStartDate = currentCycle ? new Date(currentCycle.startIso) : null
  const daysSinceCycleStart = cycleStartDate ? Math.floor((new Date() - cycleStartDate) / 86400000) : 99
  const showPaydayPrompt = daysSinceCycleStart <= 1 && accounts.length > 0 && !paydayPromptDismissed

  // ── Staleness indicator (B) ───────────────────────────────────────────
  const balancesUpdatedAt = (() => {
    try { const s = localStorage.getItem('tic_balances_updated_at_' + (user?.id || '')); return s ? new Date(s) : null } catch { return null }
  })()
  const balanceAgeDays = balancesUpdatedAt ? Math.floor((new Date() - balancesUpdatedAt) / 86400000) : null
  const balanceStale = accounts.length > 0 && (balanceAgeDays === null || balanceAgeDays >= 7)
  const balanceStaleColor = balanceAgeDays !== null && balanceAgeDays >= 7 ? colors.red : colors.gold
  const [editingSpend, setEditingSpend] = useState(null)
  const [editingIncome, setEditingIncome] = useState(null)
  const [showSpends, setShowSpends] = useState(false)
  const [showIncome, setShowIncome] = useState(false)
  const [milestone, setMilestone] = useState(null)
  const prevNetWorth = useRef(null)

  const includeCCinSTS = profile?.include_cc_in_sts === true

  const spendableAccounts = accounts.filter(a => a.type === 'current' && !a.is_emergency_fund)
  const spendableBalance = spendableAccounts.reduce((s, a) => s + (a.balance || 0), 0)
  const totalAssets = accounts.filter(a => a.type !== 'credit_card' && a.type !== 'credit').reduce((s, a) => s + (a.balance || 0), 0)
  const totalDebt = debts.filter(d => !d.is_paid_off).reduce((s, d) => s + (d.current_balance || 0), 0)
  const creditCardDebt = accounts.filter(a => a.type === 'credit_card' || a.type === 'credit').reduce((s, a) => s + (a.balance || 0), 0)
  const netWorth = totalAssets - totalDebt - creditCardDebt
  const totalIncome = selectedCycle ? incomeForCycle(selectedCycle) : 0
  const incomeNeverSet = incomeEntries.length === 0

  const upcoming = getUpcoming()
  const billsDueSoon = isCurrentCycle && selectedCycle ? upcoming.filter(b => {
    const now = new Date()
    let due = new Date(now.getFullYear(), now.getMonth(), b.due_day)
    if (due < now) due = new Date(now.getFullYear(), now.getMonth() + 1, b.due_day)
    return due <= new Date(selectedCycle.endIso)
  }) : []
  const billsDueThisWeek = isCurrentCycle ? upcoming.filter(b => {
    const now = new Date()
    let due = new Date(now.getFullYear(), now.getMonth(), b.due_day)
    if (due < now) due = new Date(now.getFullYear(), now.getMonth() + 1, b.due_day)
    const daysUntilDue = (due - now) / 86400000
    return daysUntilDue >= 0 && daysUntilDue <= 7
  }) : []
  const billsTotal = billsDueSoon.reduce((s, b) => s + (b.amount || 0), 0)

  // Credit card bills due this period - excluded from STS when toggle is off
  const creditCardIds = new Set(accounts.filter(a => a.type === 'credit_card' || a.type === 'credit').map(a => a.id))
  const ccBillsInCycle = includeCCinSTS ? 0 : (() => {
    return billsDueSoon.filter(b => creditCardIds.has(b.account_id)).reduce((s, b) => s + (b.amount || 0), 0)
  })()
  // Also exclude CC quick spends when toggle is off (quick spends tagged to a CC account)
  const allQuickSpendsList = useStore(s => s.quickSpends)
  const ccQuickSpends = includeCCinSTS ? 0 : allQuickSpendsList
    .filter(s => s.pay_cycle_start === cycleKey && creditCardIds.has(s.account_id))
    .reduce((s, e) => s + (e.amount || 0), 0)
  // Skip this month - localStorage keys per goal per cycle
  const skippedGoalIds = useMemo(() => {
    if (!currentCycle) return new Set()
    const skipped = new Set()
    goals.forEach(g => {
      try { if (localStorage.getItem(`tic_goal_skipped_${g.id}_${currentCycle.startIso}`)) skipped.add(g.id) } catch {}
    })
    return skipped
  }, [goals, currentCycle])

  const goalContributions = goals.reduce((s, g) => skippedGoalIds.has(g.id) ? s : s + (g.monthly_contribution || 0), 0)
  const plannedOutflows = goalContributions + (investmentContributions || 0)

  // Goal auto-transfer - on cycle start, move monthly_contribution from linked
  // current account to linked savings account (same pattern as bill auto-deduct).
  // localStorage key: tic_goal_transferred_{goalId}_{cycleStart}
  useEffect(() => {
    if (!currentCycle || goals.length === 0 || accounts.length === 0) return
    const now = new Date()
    const cycleStart = new Date(currentCycle.startIso)
    // Only run within first 2 days of cycle to avoid repeat triggers
    const daysSinceStart = Math.floor((now - cycleStart) / 86400000)
    if (daysSinceStart > 1) return

    const goalsToTransfer = goals.filter(g => {
      if (!g.monthly_contribution || g.monthly_contribution <= 0) return false
      if (!g.linked_account_id) return false
      if (skippedGoalIds.has(g.id)) return false
      const key = `tic_goal_transferred_${g.id}_${currentCycle.startIso}`
      return !localStorage.getItem(key)
    })

    if (goalsToTransfer.length === 0) return

    goalsToTransfer.forEach(async g => {
      const savingsAccount = accounts.find(a => a.id === g.linked_account_id)
      if (!savingsAccount) return
      // Use the explicitly chosen source account; skip transfer if none set
      if (!g.source_account_id) return
      const currentAccount = accounts.find(a => a.id === g.source_account_id)
      if (!currentAccount) return
      const key = `tic_goal_transferred_${g.id}_${currentCycle.startIso}`
      localStorage.setItem(key, '1')
      // Move money: deduct from current, add to savings
      await upsertAccount({ ...currentAccount, balance: (currentAccount.balance || 0) - g.monthly_contribution })
      await upsertAccount({ ...savingsAccount, balance: (savingsAccount.balance || 0) + g.monthly_contribution })
    })
  }, [goals, accounts, currentCycle, skippedGoalIds])
  const notifications = useMemo(() => generateNotifications({
    bills, goals, stsValue: spendableBalance - billsTotal - quickSpendTotal - plannedOutflows + quickIncomeTotal,
    cycleStart: currentCycle?.startIso, getUpcoming,
  }).filter(n => !dismissedIds.includes(n.id)), [bills, goals, spendableBalance, billsTotal, quickSpendTotal, plannedOutflows, quickIncomeTotal, currentCycle, getUpcoming, dismissedIds])

  const totalBillsInCycle = useMemo(() => {
    if (!selectedCycle) return 0
    const start = new Date(selectedCycle.startIso)
    const end = new Date(selectedCycle.endIso)
    let total = 0
    for (const bill of bills) {
      for (let m = 0; m < 3; m++) {
        const probe = new Date(start.getFullYear(), start.getMonth() + m, 1)
        const dueDate = billDueDateInMonth(bill, probe.getFullYear(), probe.getMonth())
        if (dueDate && dueDate >= start && dueDate <= end) { total += bill.amount || 0; break }
      }
    }
    return total
  }, [bills, selectedCycle])

  const cycleSpent = totalBillsInCycle + quickSpendTotal
  const rawSpendingPct = totalBudgeted > 0 ? Math.round((cycleSpent / totalBudgeted) * 100) : 0
  const spendingPct = Math.min(100, rawSpendingPct)
  const spendingStatus = rawSpendingPct > 100 ? 'Over budget' : rawSpendingPct >= 75 ? 'Getting close' : 'On track'
  const spendingColor = rawSpendingPct > 100 ? colors.red : rawSpendingPct >= 75 ? colors.gold : colors.green

  // Savings strip
  const savingsBudgeted = budgets.find(b => b.category === 'Savings')?.budgeted || 0
  const savedThisCycle = categorySpends['Savings'] || 0
  const savingsPct = savingsBudgeted > 0 ? Math.min(100, Math.round((savedThisCycle / savingsBudgeted) * 100)) : 0
  const savingsColor = savingsPct >= 100 ? colors.green : savingsPct >= 50 ? colors.gold : colors.muted
  const savingsStatus = savingsPct >= 100 ? 'Done ✓' : savingsPct > 0 ? `${savingsPct}%` : 'Not yet'

  // Basic mode: STS = account balance - bills - budget spent
  // Advanced mode: full calculation including goals, investments, quick income
  const stsValue = basicMode
    ? spendableBalance - billsTotal - quickSpendTotal
    : spendableBalance - (billsTotal - ccBillsInCycle) - (quickSpendTotal - ccQuickSpends) - plannedOutflows + quickIncomeTotal
  const daysLeft = isCurrentCycle && selectedCycle ? daysUntil(selectedCycle.endIso) : 0
  const cycleUsedPct = selectedCycle ? cyclePct(selectedCycle.startIso, selectedCycle.endIso) : 0
  const cycleLeftPct = 100 - cycleUsedPct
  const unallocated = totalIncome > 0 ? totalIncome - totalBudgeted : null

  const sortedReports = [...reports].sort((a, b) => b.month.localeCompare(a.month))
  const currentReport = sortedReports.find(r => r.month === (selectedCycle?.startIso || '').slice(0, 7))
  const prevReport = sortedReports.find(r => r !== currentReport && r.month < (currentReport?.month || (selectedCycle?.startIso || '').slice(0, 7)))
  const spendingTrendPct = (currentReport && prevReport && prevReport.total_spent > 0 && currentReport.total_spent > 0)
    ? ((currentReport.total_spent - prevReport.total_spent) / prevReport.total_spent) * 100 : null

  const stsPositive = stsValue >= 0
  const stsColor = stsValue < 0 ? colors.red : colors.green
  const ringColor = stsValue < 0 ? colors.red : colors.green

  useEffect(() => {
    if (prevNetWorth.current !== null && netWorth !== prevNetWorth.current) {
      const m = checkMilestone(netWorth, prevNetWorth.current)
      if (m) {
        const key = `milestone_seen_${m.value}`
        if (!localStorage.getItem(key)) {
          setMilestone(m)
        }
      }
    }
    prevNetWorth.current = netWorth
  }, [netWorth])

  // ── Bill auto-deduct ─────────────────────────────────────────────────────
  // When a bill with a linked account has a due date that has passed in the
  // current cycle, deduct its amount from the linked account once per cycle.
  // localStorage key: tic_bill_deducted_{billId}_{cycleStart}
  useEffect(() => {
    if (!currentCycle || bills.length === 0 || accounts.length === 0) return
    const now = new Date()
    const cycleStart = new Date(currentCycle.startIso)
    const cycleEnd = new Date(currentCycle.endIso)

    const billsToDeduct = bills.filter(b => {
      if (!b.account_id || b.is_paused) return false
      // Find due date within current cycle
      let dueDate = new Date(now.getFullYear(), now.getMonth(), b.due_day)
      if (dueDate < cycleStart) dueDate = new Date(now.getFullYear(), now.getMonth() + 1, b.due_day)
      if (dueDate > cycleEnd || dueDate > now) return false
      // Check not already deducted this cycle
      const deductKey = `tic_bill_deducted_${b.id}_${currentCycle.startIso}`
      if (localStorage.getItem(deductKey)) return false
      return true
    })

    if (billsToDeduct.length === 0) return

    billsToDeduct.forEach(async b => {
      const account = accounts.find(a => a.id === b.account_id)
      if (!account) return
      const deductKey = `tic_bill_deducted_${b.id}_${currentCycle.startIso}`
      // Mark as deducted first to prevent race conditions
      localStorage.setItem(deductKey, '1')
      await upsertAccount({ ...account, balance: (account.balance || 0) - (b.amount || 0) })
    })
  }, [bills, accounts, currentCycle])



  // ── Financial health score inputs ─────────────────────────────────────
  const emergencyAccounts = accounts.filter(a => a.is_emergency_fund)
  const emergencyBalance = emergencyAccounts.reduce((s, a) => s + (a.balance || 0), 0)
  const emergencyFundTargetMonths = profile?.emergency_fund_target_months || 6
  const monthlyExpenses = totalBudgeted || (totalIncome * 0.8) || 0
  const emergencyFundMonths = (emergencyBalance > 0 && monthlyExpenses > 0) ? emergencyBalance / monthlyExpenses : 0
  const totalDebtAll = totalDebt + creditCardDebt
  const annualIncome = totalIncome * 12
  const debtToIncome = (annualIncome > 0 && totalDebtAll > 0) ? totalDebtAll / annualIncome : 0
  const hasGoals = goals.length > 0
  const goalProgress = hasGoals ? goals.reduce((s, g) => s + (g.target_amount > 0 ? (g.current_amount / g.target_amount) * 100 : 0), 0) / goals.length : 0
  const savingsRate = (recurringMonthly > 0 && plannedOutflows > 0) ? (plannedOutflows / recurringMonthly) * 100 : 0
  const budgetOverCount = budgets.filter(b => (categorySpends[b.category] || 0) > b.budgeted).length
  const healthScore = calcHealthScore({ savingsRate, savingsRateTarget, emergencyFundMonths, emergencyFundTargetMonths, debtToIncome, goalProgress, hasGoals, budgetAdherence: budgets.length > 0 })
  const healthLabel = healthScore >= 80 ? 'Excellent' : healthScore >= 65 ? 'Good' : healthScore >= 45 ? 'Fair' : 'Needs work'
  const healthColor = healthScore >= 80 ? colors.green : healthScore >= 65 ? colors.gold : healthScore >= 45 ? '#e8a84b' : colors.red
  const healthPillars = [
    { label: 'Emergency fund', value: emergencyFundMonths >= 1 ? `${emergencyFundMonths.toFixed(1)} months covered` : emergencyBalance > 0 ? `£${Math.round(emergencyBalance)} set aside` : 'No emergency fund', ok: emergencyFundMonths >= emergencyFundTargetMonths },
    { label: 'Savings rate', value: savingsRate > 0 ? `${Math.round(savingsRate)}% of income` : 'No contributions set', ok: savingsRate >= savingsRateTarget },
    { label: 'Savings goals', value: hasGoals ? `${Math.min(100, Math.round(goalProgress))}% overall progress` : 'No goals set', ok: hasGoals && goalProgress >= 50 },
    { label: 'Debt', value: totalDebtAll > 0 ? `£${Math.round(totalDebtAll).toLocaleString()} outstanding · ${Math.round(debtToIncome * 100)}% debt-to-income` : 'Debt free', ok: totalDebtAll === 0, warn: totalDebtAll > 0 && debtToIncome < 0.2 },
    { label: 'Budget', value: budgetOverCount > 0 ? `${budgetOverCount} categor${budgetOverCount === 1 ? 'y' : 'ies'} over budget` : budgets.length > 0 ? 'All within budget' : 'No budget set', ok: budgets.length > 0 && budgetOverCount === 0 },
  ]

  const topGoals = [...goals].sort((a, b) => (a.current_amount / (a.target_amount||1)) - (b.current_amount / (b.target_amount||1))).slice(0, 2)
  const topDebts = debts.filter(d => !d.is_paid_off).slice(0, 1)
  const upcomingBills = upcoming.slice(0, 3)
  const displayName = profile?.full_name || ''
  const GOAL_COLORS = ['#3db87a', '#8b5cf6', '#4a8fe8', '#e8b84b']
  const GOAL_ICONS = ['🏦', '🏖️', '🏠', '🎯']

  // Rule-based cycle review (the analyst card at the bottom of the dashboard).
  const cycleReview = useMemo(() => buildCycleReview({
    totalBudgeted, cycleSpent, spendingPct, cycleUsedPct,
    budgets, categorySpends, bills, selectedCycle,
    billsDueThisWeekCount: billsDueThisWeek.length,
    quickSpendCount: cycleSpends.length, quickSpendTotal,
    quickIncomeTotal, stsValue,
  }), [totalBudgeted, cycleSpent, spendingPct, cycleUsedPct, budgets, categorySpends, bills, selectedCycle, billsDueThisWeek, cycleSpends, quickSpendTotal, quickIncomeTotal, stsValue])

  return (
    <div style={{ background: 'transparent', minHeight: '100%', position: 'relative', paddingBottom: '40px' }}>

      {/* Modals */}
      {showQuickSpend && <QuickSpendModal payCycleStart={cycleKey} onClose={() => setShowQuickSpend(false)} defaultTab={quickSpendTab} basicMode={basicMode} />}
      {showWalkthrough && <WalkthroughOverlay onComplete={completeWalkthrough} />}
      {showNotifications && (
        <NotificationPanel
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onDismiss={id => setDismissedIds(prev => [...prev, id])}
        />
      )}
      {editingIncome && <QuickIncomeEditModal entry={editingIncome} onSave={updateQuickIncome} onDelete={deleteQuickIncome} onClose={() => setEditingIncome(null)} />}
      {editingSpend && <QuickSpendEditModal spend={editingSpend} onSave={updateQuickSpend} onDelete={deleteQuickSpend} onClose={() => setEditingSpend(null)} />}
      {milestone && <MilestoneCelebration milestone={milestone} onDismiss={() => { localStorage.setItem(`milestone_seen_${milestone.value}`, '1'); setMilestone(null) }} />}

      {/* FAB */}
      {isMobile ? (
        <button data-walkthrough="fab" onClick={() => setShowQuickSpend(true)} style={{ position: 'fixed', bottom: '80px', right: '20px', zIndex: 40, width: '52px', height: '52px', borderRadius: '50%', background: colors.gold, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', boxShadow: `0 0 24px ${colors.glow}, 0 4px 12px rgba(0,0,0,0.45)` }}>⚡</button>
      ) : (
        <div style={{ position: 'fixed', bottom: '40px', right: 'calc(50% - 430px - 110px)', zIndex: 40 }}>
          <button data-walkthrough="fab" onClick={() => setShowQuickSpend(true)} style={{ width: '52px', height: '52px', borderRadius: '50%', background: colors.gold, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', boxShadow: `0 0 24px ${colors.glow}, 0 4px 12px rgba(0,0,0,0.45)` }}>⚡</button>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '18px 20px 14px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text, letterSpacing: '-0.01em' }}>{greeting(displayName)}</div>
          <button onClick={() => setShowCyclePicker(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginTop: '3px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>Pay period: {selectedCycle ? cycleRangeLabel(selectedCycle) : ' - '}</span>
            <span style={{ fontSize: '12px', color: colors.muted }}>▾</span>
          </button>
        </div>
        <button onClick={() => setShowNotifications(true)} style={{ position: 'absolute', right: '16px', top: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px' }}>
          🔔
          {notifications.length > 0 && (
            <div style={{ position: 'absolute', top: '-3px', right: '-3px', width: '16px', height: '16px', borderRadius: '50%', background: colors.red, border: '2px solid #0c1520', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>{notifications.length > 9 ? '9+' : notifications.length}</div>
          )}
        </button>
      </div>

      {/* Inline cycle picker modal */}
      {showCyclePicker && (
        <div onClick={() => setShowCyclePicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, width: '100%', maxWidth: '480px', margin: '0 20px', borderRadius: '24px', padding: '8px 0 24px', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '8px auto 16px' }} />
            <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.text, padding: '0 20px 14px' }}>Select period</div>
            {filteredCycles.map(c => {
              const active = c.startIso === (selectedCycleStart || currentCycle?.startIso)
              return (
                <button key={c.startIso} onClick={() => { setSelectedCycleStart(c.startIso); setShowCyclePicker(false) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 20px', background: active ? 'rgba(201,151,43,0.08)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', fontFamily: fonts.body }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: active ? colors.gold : colors.text, textAlign: 'left' }}>{c.label}</div>
                    <div style={{ fontSize: '13px', color: colors.muted, marginTop: '2px', textAlign: 'left' }}>{c.rangeLabel}</div>
                  </div>
                  {active && <span style={{ color: colors.gold, fontSize: '16px' }}>✓</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div style={{ padding: '0 16px' }}>

        {/* ── Payday prompt (A) ─────────────────────────────────────────── */}
        {showPaydayPrompt && (
          <div style={{ background: 'linear-gradient(135deg, rgba(201,151,43,0.18), rgba(201,151,43,0.08))', border: '1px solid rgba(201,151,43,0.35)', borderRadius: '16px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '24px', flexShrink: 0 }}>🎉</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: colors.gold }}>It's payday!</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Update your account balances so your safe-to-spend is accurate.</div>
            </div>
            <button onClick={() => onNavigate?.('budget')} style={{ background: colors.gold, border: 'none', borderRadius: '10px', padding: '8px 14px', fontFamily: fonts.body, fontSize: '13px', fontWeight: 700, color: colors.onAccent, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Update</button>
            <button onClick={dismissPaydayPrompt} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: '4px', flexShrink: 0 }}>×</button>
          </div>
        )}

        {/* ── Income prompt ─────────────────────────────────────────────── */}
        {incomeNeverSet && (
          <div onClick={() => onNavigate?.('income')} style={{ background: 'rgba(74,143,232,0.1)', border: '1px solid rgba(74,143,232,0.25)', borderRadius: '16px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <div style={{ fontSize: '24px', flexShrink: 0 }}>💷</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: colors.blue }}>Set your income</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Add your income to unlock Safe to Spend and budget tracking.</div>
            </div>
            <div style={{ fontSize: '18px', color: colors.blue }}>›</div>
          </div>
        )}

        {/* ── Tour prompt ──────────────────────────────────────────────── */}
        {!tourPromptDismissed && accounts.length > 0 && (
          <div style={{ background: 'linear-gradient(135deg, rgba(201,151,43,0.12), rgba(201,151,43,0.04))', border: '1px solid rgba(201,151,43,0.25)', borderRadius: '16px', padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '24px', flexShrink: 0 }}>🗺️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: colors.gold }}>New to TIC Tracker?</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Take a 60-second tour of your dashboard.</div>
            </div>
            <button onClick={startWalkthrough} style={{ background: colors.gold, border: 'none', borderRadius: '10px', padding: '8px 14px', fontFamily: fonts.body, fontSize: '13px', fontWeight: 700, color: colors.onAccent, cursor: 'pointer', flexShrink: 0 }}>Start</button>
            <button onClick={() => { setTourPromptDismissed(true); try { localStorage.setItem(`tic_walkthrough_complete_${user?.id || ''}`, '1') } catch {} }} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: '20px', padding: '4px', flexShrink: 0 }}>×</button>
          </div>
        )}

        {/* ── STS hero ────────────────────────────────────────────────── */}
        <div data-walkthrough="sts" style={{
          background: stsValue < 0 ? 'linear-gradient(135deg,#1e0a0a,#2d1010)' : 'linear-gradient(135deg,#0f1e35,#162844)',
          border: `1px solid ${stsValue < 0 ? 'rgba(224,82,82,0.3)' : 'rgba(255,255,255,0.08)'}`,
          borderLeft: `4px solid ${stsColor}`,
          borderRadius: '20px', padding: '20px', marginBottom: '14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.08em', color: `rgba(245,243,239,0.72)`, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                SAFE TO SPEND
                <Hint title="Safe to Spend" text="Your account balances, minus bills due this period, minus your budgeted spending, plus any quick income logged. Updates when you refresh your account balances." />
              </div>
              <div style={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: '48px', color: stsColor, lineHeight: 1, letterSpacing: '-0.02em' }}>
                {stsValue < 0 ? '-' : ''}{fmt(Math.abs(stsValue))}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: stsColor, marginTop: '4px', opacity: 0.85 }}>
                {stsValue < 0 ? 'over committed' : 'until payday'}
              </div>
              {!basicMode && !includeCCinSTS && creditCardIds.size > 0 && (ccQuickSpends > 0 || ccBillsInCycle > 0) && (
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: 'rgba(224,82,82,0.8)', background: 'rgba(224,82,82,0.08)', border: '1px solid rgba(224,82,82,0.2)', borderRadius: '6px', padding: '2px 7px' }}>
                    💳 CC spend not included
                  </span>
                  <Hint title="Credit card spending" text="CC spending is excluded from Safe to Spend by default - the money hasn't left your current account yet. You can change this in Settings → Include CC bills in Safe to Spend." />
                </div>
              )}
              {balanceStale && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', padding: '6px 10px', background: `${balanceStaleColor}15`, border: `1px solid ${balanceStaleColor}35`, borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px' }}>⚠️</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: balanceStaleColor }}>
                    {balanceAgeDays === null ? 'Balances never updated' : `Balance updated ${balanceAgeDays} day${balanceAgeDays !== 1 ? 's' : ''} ago`}
                  </span>
                </div>
              )}
            </div>
            {isCurrentCycle && selectedCycle && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: `rgba(245,243,239,0.4)` }}>PAYDAY IN</div>
                <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '28px', color: '#f5f3ef', lineHeight: 1 }}>{daysLeft}</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: `rgba(245,243,239,0.72)` }}>days</div>
                <div style={{ fontSize: '12px', fontWeight: 500, color: `rgba(245,243,239,0.62)`, marginBottom: '6px' }}>
                  {new Date(selectedCycle.endIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
            )}
          </div>

          {/* STS Breakdown */}
          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {[
              { label: '🏦 Current accounts', value: spendableBalance, color: '#f5f3ef' },
              { label: '📅 Bills due this period', value: -billsTotal, color: billsTotal > 0 ? colors.red : 'rgba(245,243,239,0.66)' },
              { label: '⚡ Quick spends', value: -quickSpendTotal, color: quickSpendTotal > 0 ? colors.red : 'rgba(245,243,239,0.66)' },
              ...(!basicMode && goalContributions > 0 ? [{ label: '🎯 Goal contributions', value: -goalContributions, color: colors.red }] : []),
              ...(!basicMode && quickIncomeTotal > 0 ? [{ label: '💰 Quick income', value: quickIncomeTotal, color: colors.green }] : []),
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <div style={{ fontSize: '12px', fontWeight: 500, color: `rgba(245,243,239,0.5)` }}>{row.label}</div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: row.value >= 0 ? (row.value === 0 ? 'rgba(245,243,239,0.66)' : '#f5f3ef') : colors.red, fontFamily: fonts.mono || fonts.body }}>
                  {row.value >= 0 ? fmt(row.value) : `-${fmt(Math.abs(row.value))}`}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: `rgba(245,243,239,0.5)` }}>SAFE TO SPEND</div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: stsColor, fontFamily: fonts.heading }}>{stsValue < 0 ? `-${fmt(Math.abs(stsValue))}` : fmt(stsValue)}</div>
            </div>
          </div>
        </div>

        {/* ── Quick spend log ──────────────────────────────────────────── */}
        {/* ── Spending + savings strip ─────────────────────────────────── */}
        {totalBudgeted > 0 && (
          <div onClick={() => onNavigate?.('budget')} style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, padding: '12px 16px', marginBottom: '14px', cursor: 'pointer' }}>

            {/* Spending row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: savingsBudgeted > 0 ? '10px' : 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: colors.muted, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>BUDGET SPENT <Hint title="Budget Spent" text="Total quick spends and bills logged this period vs your total budgeted amount across all categories." /></div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text, flexShrink: 0 }}>{fmt(cycleSpent)}</div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: colors.muted, flexShrink: 0 }}>of {fmt(totalBudgeted)}</div>
              <div style={{ flex: 1, height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${spendingPct}%`, background: spendingColor, borderRadius: '2px' }} />
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: spendingColor, flexShrink: 0 }}>{spendingStatus}</div>
            </div>

            {/* Savings row - only if savings budget is set */}
            {savingsBudgeted > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: colors.muted, flexShrink: 0, width: '44px' }}>SAVED</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text, flexShrink: 0 }}>{fmt(savedThisCycle)}</div>
                <div style={{ fontSize: '12px', fontWeight: 500, color: colors.muted, flexShrink: 0 }}>of {fmt(savingsBudgeted)}</div>
                <div style={{ flex: 1, height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${savingsPct}%`, background: savingsColor, borderRadius: '2px' }} />
                </div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: savingsColor, flexShrink: 0 }}>{savingsStatus}</div>
              </div>
            )}
            <div style={{ fontSize: '12px', fontWeight: 500, color: `rgba(${colors.textRgb},0.55)`, marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '8px' }}>
              Tap to see full budget breakdown →
            </div>
          </div>
        )}

        {!basicMode && cycleIncome.length > 0 && (
          <div style={{ background: colors.card, border: '1px solid rgba(61,184,122,0.2)', borderRadius: '16px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px', overflow: 'hidden' }}>
            <button onClick={() => setShowIncome(s => !s)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>💰</span>
                <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', color: colors.text }}>QUICK INCOME</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: colors.green, background: 'rgba(61,184,122,0.1)', border: '1px solid rgba(61,184,122,0.2)', borderRadius: '10px', padding: '2px 8px' }}>{cycleIncome.length}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: fonts.heading, fontSize: '15px', fontWeight: 700, color: colors.green }}>+{fmt(quickIncomeTotal)}</span>
                <span style={{ fontSize: '13px', color: colors.muted, display: 'inline-block', transform: showIncome ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
              </div>
            </button>
            {showIncome && cycleIncome.map((entry, i) => (
              <div key={entry.id || i} onClick={() => setEditingIncome(entry)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
                <IC emoji={entry.category ? '💰' : '💰'} color={colors.green} size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{entry.note || entry.category || 'Quick income'}</div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '1px' }}>
                    {entry.category && entry.note && <div style={{ fontSize: '12px', color: colors.green, fontWeight: 600 }}>{entry.category}</div>}
                    {entry.owner_label && entry.owner_label !== 'Joint' && <div style={{ fontSize: '12px', color: colors.muted, fontWeight: 500 }}>{entry.owner_label}</div>}
                  </div>
                </div>
                <div style={{ fontFamily: fonts.heading, fontSize: '15px', fontWeight: 700, color: colors.green }}>+{fmt(entry.amount)}</div>
                <div style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
              </div>
            ))}
            <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <button onClick={() => { setQuickSpendTab('income'); setShowQuickSpend(true) }} style={{ width: '100%', padding: '10px', background: 'rgba(61,184,122,0.06)', border: '1px dashed rgba(61,184,122,0.25)', borderRadius: '8px', color: colors.green, fontFamily: fonts.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>+ Log income</button>
            </div>
          </div>
        )}

        {cycleSpends.length > 0 && (
          <div style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px', overflow: 'hidden' }}>
            <button onClick={() => setShowSpends(s => !s)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>⚡</span>
                <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', color: colors.text }}>QUICK SPENDS</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: colors.gold, background: 'rgba(201,151,43,0.1)', border: '1px solid rgba(201,151,43,0.2)', borderRadius: '10px', padding: '2px 8px' }}>{cycleSpends.length}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: fonts.heading, fontSize: '15px', fontWeight: 700, color: colors.text }}>{fmt(quickSpendTotal)}</span>
                <span style={{ fontSize: '13px', color: colors.muted, display: 'inline-block', transform: showSpends ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
              </div>
            </button>
            {showSpends && cycleSpends.map((spend, i) => (
              <div key={spend.id || i} onClick={() => setEditingSpend(spend)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
                <IC emoji={spend.budget_category ? CAT_ICON(spend.budget_category) : '💸'} color={colors.gold} size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{spend.note || 'Quick spend'}</div>
                  {spend.budget_category && <div style={{ fontSize: '12px', color: colors.gold, marginTop: '1px', fontWeight: 600 }}>{spend.budget_category}</div>}
                </div>
                <div style={{ fontFamily: fonts.heading, fontSize: '15px', fontWeight: 700, color: colors.text }}>{fmt(spend.amount)}</div>
                <div style={{ fontSize: '14px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
              </div>
            ))}
            <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <button onClick={() => setShowQuickSpend(true)} style={{ width: '100%', padding: '10px', background: 'rgba(201,151,43,0.08)', border: '1px dashed rgba(201,151,43,0.25)', borderRadius: '8px', color: colors.gold, fontFamily: fonts.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>+ Log another spend</button>
            </div>
          </div>
        )}

        {/* ── 4 metric tiles ──────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <Tile emoji="👛" iconColor={colors.gold} label="TOTAL BUDGETED" value={fmt(totalBudgeted)} sub={totalIncome > 0 ? `of ${fmt(totalIncome + quickIncomeTotal)} income` : 'No income set'} accent={colors.gold} onClick={() => onNavigate?.('budget')} valueColor={totalIncome > 0 && totalBudgeted > (totalIncome + quickIncomeTotal) ? colors.red : colors.text} />
          <Tile emoji="📅" iconColor={colors.red} label="BILLS DUE" value={fmt(billsDueThisWeek.reduce((s, b) => s + b.amount, 0))} sub={`${billsDueThisWeek.length} due this week`} accent={colors.red} onClick={() => onNavigate?.('bills')} valueColor={billsDueThisWeek.length > 0 ? colors.red : colors.text} />
          <Tile
            emoji="🥧"
            iconColor={colors.purple}
            label="BUDGET GAP"
            value={unallocated === null ? ' - ' : `${unallocated < 0 ? '-' : ''}${fmt(Math.abs(unallocated))}`}
            sub={unallocated === null ? 'Set income first' : unallocated < 0 ? `⚠️ Exceeds income by ${fmt(Math.abs(unallocated))}` : `💡 ${fmt(unallocated)} isn't budgeted yet`}
            accent={colors.purple}
            onClick={() => onNavigate?.('budget')}
            valueColor={unallocated !== null && unallocated < 0 ? colors.red : colors.text}
            hint={{ title: 'Budget Gap', text: 'Your income minus your total budgeted amount. Positive means you have income left to assign. Negative means your budget exceeds your income.' }}
          />
          {!basicMode && <Tile emoji="📈" iconColor={colors.green} label="SPENDING TREND" value={spendingTrendPct === null ? ' - ' : `${spendingTrendPct > 0 ? '+' : ''}${Math.round(spendingTrendPct)}%`} sub="vs last period" accent={spendingTrendPct === null ? 'rgba(255,255,255,0.1)' : spendingTrendPct <= 0 ? colors.green : colors.red} onClick={() => onNavigate?.('insights')} valueColor={spendingTrendPct === null ? colors.muted : spendingTrendPct <= 0 ? colors.green : colors.red} />}
        </div>

        {/* ── Net Worth ─────────────────────────────────────────────── */}
        {!basicMode && accounts.length > 0 && (
          <div style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, padding: '16px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>NET WORTH</div>
              <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: netWorth >= 0 ? colors.green : colors.red, lineHeight: 1 }}>
                {netWorth < 0 ? '-' : ''}{fmt(Math.abs(netWorth))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: 0 }}>
              <div style={{ paddingRight: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: colors.muted, marginBottom: '3px' }}>Total assets</div>
                <div style={{ fontFamily: fonts.heading, fontSize: '17px', fontWeight: 700, color: colors.text }}>{fmt(totalAssets)}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ paddingLeft: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: colors.muted, marginBottom: '3px' }}>Total debt</div>
                <div style={{ fontFamily: fonts.heading, fontSize: '17px', fontWeight: 700, color: (totalDebt + creditCardDebt) > 0 ? colors.red : colors.muted }}>{(totalDebt + creditCardDebt) > 0 ? fmt(totalDebt + creditCardDebt) : ' - '}</div>
              </div>
            </div>
            <div style={{ height: '4px', borderRadius: '2px', marginTop: '12px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: totalAssets > 0 ? `${Math.min(100, Math.max(0, (netWorth / totalAssets) * 100))}%` : '0%', background: netWorth >= 0 ? colors.green : colors.red, borderRadius: '2px' }} />
            </div>
          </div>
        )}

                {/* ── Upcoming Bills ───────────────────────────────────────────── */}
        {upcomingBills.length > 0 && (
          <div style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>UPCOMING BILLS</div>
              <button onClick={() => onNavigate?.('bills')} style={{ fontSize: '13px', fontWeight: 600, color: colors.gold, background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
            </div>
            {upcomingBills.map((bill, i) => {
              const now = new Date()
              let due = new Date(now.getFullYear(), now.getMonth(), bill.due_day)
              if (due < now) due = new Date(now.getFullYear(), now.getMonth() + 1, bill.due_day)
              const days = Math.ceil((due - now) / 86400000)
              const daysLabel = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`
              return (
                <div key={bill.id || i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <BillLogo name={bill.name} category={bill.category} size={44} radius={22} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>{bill.name}</div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>{bill.category} · Due {bill.due_day} {due.toLocaleString('en-GB', { month: 'short' })}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: fonts.heading, fontSize: '17px', fontWeight: 700, color: colors.text }}>{fmt(bill.amount)}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: days <= 3 ? colors.red : colors.gold, marginTop: '2px' }}>{daysLabel}</div>
                  </div>
                  <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                </div>
              )
            })}
          </div>
        )}

        {/* ── Goals Progress ───────────────────────────────────────────── */}
        {(topGoals.length > 0 || topDebts.length > 0) && (
          <div style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>GOALS PROGRESS</div>
              <button onClick={() => onNavigate?.('goals')} style={{ fontSize: '13px', fontWeight: 600, color: colors.gold, background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
            </div>
            {[...topGoals.map((g, i) => ({ type: 'goal', data: g, idx: i })), ...topDebts.map((d, i) => ({ type: 'debt', data: d, idx: i }))].map(({ type, data, idx }) => {
              const isDebt = type === 'debt'
              const paid = isDebt ? ((data.original_balance || 0) - (data.current_balance || 0)) : 0
              const pctVal = isDebt ? (data.original_balance > 0 ? Math.round((paid / data.original_balance) * 100) : 0)
                : (data.target_amount > 0 ? Math.round((data.current_amount / data.target_amount) * 100) : 0)
              const gc = isDebt ? colors.red : GOAL_COLORS[idx % GOAL_COLORS.length]
              const icon = isDebt ? '💳' : data.is_emergency_fund ? '🛡️' : GOAL_ICONS[idx % GOAL_ICONS.length]
              return (
                <div key={data.id} style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                    <IC emoji={icon} color={gc} size={44} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>{data.name}</div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>
                        {isDebt ? `${fmt(paid)} paid · ${fmt(data.current_balance || 0)} remaining` : `${fmt(data.current_amount || 0)} of ${fmt(data.target_amount || 0)}`}
                      </div>
                    </div>
                    <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 800, color: gc }}>{pctVal}%</div>
                    <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                  </div>
                  <div style={{ height: '5px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, pctVal)}%`, background: gc, borderRadius: '3px' }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}


        {/* ── Financial Health Score ──────────────────────────────────────── */}
        {!basicMode && <div data-walkthrough="health-score" style={{ background: colors.card, border: `1px solid ${colors.borderGold}`, borderRadius: '16px', boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, overflow: 'hidden', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, display: 'flex', alignItems: 'center', gap: '6px' }}>FINANCIAL HEALTH <Hint title="Financial Health Score" text="Scored 0-100 across five pillars: emergency fund, savings rate, goals progress, debt, and budget discipline. Higher is better." /></div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: healthColor }}>{healthLabel}</div>
          </div>
          {/* Score ring + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '4px 16px 16px' }}>
            <div style={{ position: 'relative', width: '80px', height: '80px', flexShrink: 0 }}>
              <svg width="80" height="80" style={{ transform: 'rotate(-90deg)', position: 'absolute', inset: 0 }}>
                <circle cx="40" cy="40" r="33" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" />
                <circle cx="40" cy="40" r="33" fill="none" stroke={healthColor} strokeWidth="7" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 33} strokeDashoffset={2 * Math.PI * 33 - (healthScore / 100) * 2 * Math.PI * 33} />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 800, color: healthColor, lineHeight: 1 }}>{healthScore}</div>
                <div style={{ fontSize: '11px', color: `rgba(${colors.textRgb},0.62)`, fontWeight: 600, marginTop: '2px' }}>/100</div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: healthColor, lineHeight: 1 }}>{healthLabel}</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '4px', lineHeight: 1.5 }}>
                {healthScore >= 80 ? 'Your finances are in great shape.' : healthScore >= 65 ? 'Solid foundations - a few areas to improve.' : healthScore >= 45 ? 'Making progress - some key areas need attention.' : 'A few fundamentals to work on.'}
              </div>
            </div>
          </div>
          {/* Pillar breakdown */}
          {healthPillars.map((p, i) => {
            const dotColor = p.ok ? colors.green : p.warn ? colors.gold : colors.red
            const iconChar = p.ok ? '✓' : p.warn ? '!' : '!'
            return (
            <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 16px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{p.label}</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '1px' }}>{p.value}</div>
              </div>
              <div style={{ fontSize: '16px', color: dotColor, fontWeight: 700 }}>{iconChar}</div>
            </div>
            )
          })}
        </div>}

        {/* ── Path to Investing ──────────────────────────────────────── */}
        {!basicMode && <ReadinessCoach
          signals={{
            emergencyBalance,
            emergencyFundMonths,
            emergencyTargetMonths: emergencyFundTargetMonths,
            debts: debts.filter(d => !d.is_paid_off),
            totalDebt,
            savingsRate,
            savingsRateTarget,
          }}
          onNavigate={onNavigate}
        />}

        {/* ── This cycle in review (rule-based analyst) ──────────────── */}
        <CycleReview review={cycleReview} cycleLabel={selectedCycle ? cycleRangeLabel(selectedCycle) : null} />

        {/* ── Put another way (fun real-terms comparisons) ───────────── */}
        <SpendingInContext spend={cycleSpent} />

        {/* Empty state */}
        {accounts.length === 0 && (
          <div style={{ background: colors.card, border: '1px dashed rgba(201,151,43,0.3)', borderRadius: '16px', padding: '28px 20px', textAlign: 'center', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏦</div>
            <div style={{ fontFamily: fonts.heading, fontSize: '17px', fontWeight: 700, color: colors.text, marginBottom: '6px' }}>Add your accounts</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6 }}>Head to Budget to add your current accounts and see your safe-to-spend figure.</div>
          </div>
        )}

      </div>
    </div>
  )
}
